import React from 'react';

import { Icon, Text, Box, Button, Flex } from '@opendoor/bricks/core';
import { FlexProps } from '@opendoor/bricks/core/Flex';
import colors from '@opendoor/bricks/theme/eero/foundations/colors';
import { isOpendoorAppWebView } from 'shared/helpers/globals';
import { InternalLink } from 'platform/react-router';

type BaseOverviewSubtitleItem = {
  /** Main text of the item */
  text: string;
  /** Subtext of the item (placed below `text`) */
  subtext: string;
};

type ButtonOverviewSubtitleItem = BaseOverviewSubtitleItem & {
  type: 'button';
  /** Callback on clicking the `text` of an item. If passed in for an item, style the item differently. */
  onClick: () => void;
  /** Text to label the button */
  ariaLabel: string;
};

type LinkOverviewSubtitleItem = BaseOverviewSubtitleItem & {
  type: 'internal-link';
  /** Link to navigate when user clicks the link */
  href: string;
  /** Text to label the link */
  ariaLabel: string;
};

type NonInteractiveOverviewSubtitleItem = BaseOverviewSubtitleItem & {
  type: 'non-interactive';
};

export type SubtitleItemProps =
  | NonInteractiveOverviewSubtitleItem
  | LinkOverviewSubtitleItem
  | ButtonOverviewSubtitleItem;

export interface IOverviewHeaderSubtitle {
  /** List of subtitle items to render */
  items: Array<SubtitleItemProps>;
}

/**
 * OverviewHeaderSubtitle displays the subtitle items for the OverviewHeader component
 * component-test-check-ignore
 */
export const OverviewHeaderSubtitle: React.FC<IOverviewHeaderSubtitle & FlexProps> = ({
  items,
  ...flexProps
}) => (
  <Flex as="dl" flexWrap="wrap" {...flexProps}>
    {items.map((itemData: SubtitleItemProps) => (
      <Flex
        key={itemData.text}
        flexDirection="column"
        pr={7}
        mr={7}
        mb={isOpendoorAppWebView ? 5 : undefined}
        _notLast={{ borderRight: `1px solid ${colors.neutrals40}` }}
        data-testid="overview-header-subtitle-item"
      >
        <Box as="dt">
          <SubtitleItemContent {...itemData} />
        </Box>
        <Text as="dd" size="s0" ml="0">
          {itemData.subtext}
        </Text>
      </Flex>
    ))}
  </Flex>
);

/**
 * Renders the content of the subtitle based on its type:
 *
 * non-interactive:
 *    - Renders as text
 *
 * button:
 *    - Renders as a <Button>
 *    - Tracks click events using `cake-closing-overview-header-subtitle-{text}`
 *
 * internal-link:
 *    - Renders as <a>
 *    - Uses client-side navigation
 *    - Tracks click events using `cake-closing-overview-header-subtitle-{text}`
 */
const SubtitleItemContent: React.FC<SubtitleItemProps> = (props) => {
  const analyticsName = `cake-closing-overview-header-subtitle-${props.text
    .toLowerCase()
    .split(' ')
    .join('-')}`;

  const children = (
    <>
      {props.text}
      <Icon name="chevron-right" size={16} color="brand50" />
    </>
  );

  if (props.type === 'button') {
    return (
      <Button
        analyticsName={analyticsName}
        onClick={props.onClick}
        aria-label={props.ariaLabel}
        fontSize="s0"
        lineHeight="s000"
        fontWeight="semibold"
        variant="link"
        height="auto"
        p={[0, 0, 0]}
      >
        {children}
      </Button>
    );
  }

  if (props.type === 'internal-link') {
    return (
      <InternalLink
        to={props.href}
        analyticsName={analyticsName}
        aria-label={props.ariaLabel}
        fontSize="s0"
        lineHeight="s000"
        fontWeight="semibold"
        display="flex"
      >
        {children}
      </InternalLink>
    );
  }

  // non-interactive
  return (
    <Text fontSize="s0" lineHeight="s000" fontWeight="semibold">
      {props.text}
    </Text>
  );
};
